import {
  Button,
  cn,
  Divider,
  InputLabel,
  SplitTextField,
  TextField,
  toast,
  Typography,
} from "@suraasa/placebo-ui"
import { useMutation, useQuery } from "@tanstack/react-query"
import api from "api"
import { SuraasaPlusProduct } from "api/resources/suraasaPlus/types"
import { FetchUserResponse, PhoneNumber } from "api/resources/users/types"
import clsx from "clsx"
import AsyncBuilder from "components/AsyncBuilder"
import GoogleLocationSelect, {
  AddressComponents,
  AddressTypeEnum,
} from "components/GoogleLocationSelect.tsx"
import PageContainer from "components/PageContainer"
import { AuthManager } from "features/Authentication/AuthManager"
import { getPurchaseDetails } from "features/Payment/CartlessSubscriptionFlow"
import AlreadyPaidScreen from "features/Payment/CartlessSubscriptionFlow/AlreadyPaidScreen"
import PaymentUnderProcess from "features/Payment/CartlessSubscriptionFlow/PaymentUnderProcess"
import { useSubscriptionPayment } from "features/Payment/CartlessSubscriptionFlow/useSubscriptionPayment"
import { context, useGlobalContext } from "global/Context/context"
import { useContext, useEffect, useState } from "react"
import { useGoogleReCaptcha } from "react-google-recaptcha-hook"
import { Controller, useForm } from "react-hook-form"
import { useSearchParams } from "react-router-dom"
import routes from "routes"
import { safeCaptcha } from "utils/captcha"
import { SPNY_26_SLUG } from "utils/constants"
import countryCodes from "utils/countryCodes"
import { buildPhoneNumber, buildUserName, mapErrorsV2 } from "utils/helpers"
import useCurrentCountry from "utils/hooks/useCurrentCountry"
import { useEnrollments } from "utils/hooks/useEnrollments"
import useHideGlobalBottomNavigation from "utils/hooks/useGlobalHideBottomNavigation"
import { CaptchaType } from "utils/hooks/useGRecaptcha"
import useHideTopNavigation from "utils/hooks/useHideTopNavigation"
import { getCurrentPlatform } from "utils/platform"
import { createLead, trackingService } from "utils/tracking"
import { TrackingEvents } from "utils/tracking/events"
import { isEmail } from "validator"

import { useSuraasaPlusContext } from "../../context"
import ProductBanner from "../product-banner"
import SelectPlan from "../single-page/SelectPlan"
import { getPlanModeBySubscriptionIntervalType, PlanMode } from "../utils"

const validPlans = Object.values(PlanMode)

type FormType = {
  fullName: string
  email: string
  phoneNumber: PhoneNumber
  promocode?: string
  // Only used when currentCountry === null
  location?: AddressComponents | null
}

const options = countryCodes.map(({ dialCode }) => ({
  value: dialCode,
  label: dialCode,
}))

const SinglePageCheckout = () => {
  useHideTopNavigation()
  useHideGlobalBottomNavigation()

  const platform = getCurrentPlatform()
  const authInfo = AuthManager.getPlatformAuth()
  const { profile } = useContext(context)

  const [selectedPlan, setSelectedPlan] = useState<PlanMode>()

  const [searchParams, setSearchParams] = useSearchParams()
  const initialPromocode =
    searchParams.get("promocode") || searchParams.get("promo") || undefined

  const [showPromoCodeInput, setShowPromoCodeInput] = useState<boolean>(
    !!initialPromocode
  )

  // Track flow view on initial load
  useEffect(() => {
    trackingService.trackEvent(
      TrackingEvents.suraasaPlusFlowViewed,
      {},
      {
        services: ["PostHog"],
      }
    )

    // Clean up old order_token from URL. Only run this once on mount
    if (searchParams.get("order_token")) {
      searchParams.delete("order_token")
      setSearchParams(searchParams, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const {
    register,
    handleSubmit,
    control,
    setValue,
    clearErrors,
    setError,
    getValues,
    formState: { errors },
  } = useForm<FormType>({
    defaultValues: {
      fullName: authInfo
        ? buildUserName(authInfo.user)
        : searchParams.get("full_name") || "",
      email: authInfo?.user.email || searchParams.get("email") || "",
      phoneNumber: {
        code: searchParams.get("dial_code")
          ? `+${searchParams.get("dial_code")?.replaceAll("+", "")}`
          : "",
        number: searchParams.get("phone_number") || "",
      },
      promocode: initialPromocode,
    },
  })

  const currentCountry = useCurrentCountry()

  const productGroupQuery = useMutation({
    mutationKey: ["suraasa-plus-product-group", SPNY_26_SLUG],
    mutationFn: (data: { promocode?: string | null }) =>
      api.suraasaPlus.retrieveProductGroup({
        urlParams: {
          slug: SPNY_26_SLUG,
        },
        data,
      }),
    onSuccess: (response, input) => {
      if (input.promocode) {
        if (
          response.products.find(
            p => p.price.promocode?.code !== input.promocode
          )
        ) {
          toast.error("Invalid promo code")
          return
        }
      }
    },
    onError: e => mapErrorsV2(e),
  })

  useEffect(() => {
    productGroupQuery.mutate({
      promocode: initialPromocode,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const plansToShow = (() => {
    const plans = (searchParams.get("plans") ?? "")
      .split(",")
      .filter(x => validPlans.includes(x as PlanMode)) as PlanMode[]

    // Ensuring Annual Plan is always included
    return plans.filter(x => x !== PlanMode.ANNUAL).length > 0
      ? [PlanMode.ANNUAL, ...plans]
      : [PlanMode.ANNUAL]
  })()

  const products = (productGroupQuery.data?.products || [])
    .filter(product => {
      const plan = getPlanModeBySubscriptionIntervalType(
        product.subscriptionDetails!
      )
      if (!plan) return false

      // Show only plans present in the "plans" query param
      return plansToShow.includes(plan)
    })
    .sort((a, b) => {
      const order = [PlanMode.ANNUAL, PlanMode.QUARTERLY, PlanMode.MONTHLY]
      const planA = getPlanModeBySubscriptionIntervalType(
        a.subscriptionDetails!
      )!
      const planB = getPlanModeBySubscriptionIntervalType(
        b.subscriptionDetails!
      )!
      return order.indexOf(planA) - order.indexOf(planB)
    })

  useEffect(() => {
    if (productGroupQuery.isSuccess && !selectedPlan) {
      if (!productGroupQuery.data.products.length) {
        toast.error("No products found in the product group")
        throw new Error("No products found in the product group")
      }

      const defaultSelectedPlan = (() => {
        const fromURL = searchParams.get("default_selected_plan") || ""
        if (!validPlans.includes(fromURL as PlanMode)) return PlanMode.ANNUAL

        return fromURL as PlanMode
      })()

      if (
        products.find(
          x =>
            x.subscriptionDetails &&
            getPlanModeBySubscriptionIntervalType(x.subscriptionDetails) ===
              defaultSelectedPlan
        )
      ) {
        setSelectedPlan(defaultSelectedPlan)
        return
      }

      setSelectedPlan(PlanMode.ANNUAL)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productGroupQuery.isSuccess])

  const selectedProduct = (productGroupQuery.data?.products || [])
    .filter(x => !!x.subscriptionDetails)
    .find(
      p =>
        p.subscriptionDetails &&
        getPlanModeBySubscriptionIntervalType(p.subscriptionDetails) ===
          selectedPlan
    )

  const promo = selectedProduct?.price?.promocode

  const [paymentUnderProcess, setPaymentUnderProcess] = useState(false)

  const purchaseDetails = selectedProduct
    ? getPurchaseDetails(selectedProduct, {
        currency: selectedProduct.price.currency,
        finalAmount: promo
          ? promo.priceAfterDiscount
          : selectedProduct.price.price,
      })
    : null

  const payment = useSubscriptionPayment({
    purchaseDetails,
    logoSrc:
      "https://res.cloudinary.com/diij4p2ms/image/upload/w_48/h_48/product-thumbnails/suraasa-plus.webp",
    stripeConfig: {
      redirectRoute: (() => {
        const successRedirectPage = new URL(
          routes.suraasaPlus.paymentSuccess,
          window.location.origin
        )
        return successRedirectPage.toString()
      })(),
    },
    razorpayConfig: {
      onSuccess: () => {
        const params = new URLSearchParams(window.location.search)
        params.set("order_status", "success")

        const successRedirectPage = new URL(
          routes.suraasaPlus.paymentSuccess,
          window.location.origin
        )

        successRedirectPage.search = params.toString()
        window.location.replace(successRedirectPage.toString())
      },
      getUserDetails: () => {
        const details = {
          email: getValues("email"),
          name: getValues("fullName"),
          contact: getValues("phoneNumber").number
            ? buildPhoneNumber(getValues("phoneNumber"))
            : undefined,
        }
        return details
      },
    },
  })

  const { isActive, refreshSubscriptionData } = useSuraasaPlusContext()

  useQuery({
    queryKey: ["subscription-status-check"],
    enabled: paymentUnderProcess,
    refetchInterval: 3000,
    queryFn: async () => {
      // By default, react query stops polling if API returns an error.
      // Wrapping in try catch so that in case of any error the polling does not stop.
      try {
        await refreshSubscriptionData()
      } catch (error) {
        console.error(error)
      }
    },
  })

  const enrollments = useEnrollments()

  /**
   * Billing Information Component Start
   */

  const country = useCurrentCountry()

  const defaultDialCode = countryCodes.find(
    item => item.code === country?.isoCode
  )?.dialCode

  // Pre-fill phone number value. Order of preference: authInfo, profile, defaultDialCode
  useEffect(() => {
    const filledPhoneNumber = getValues("phoneNumber")
    // console.log("Pre-filling phone number", {
    //   alreadyFilled: filledPhoneNumber,
    //   number: profile.phoneNumber,
    //   defaultDialCode,
    // })

    if (filledPhoneNumber?.code && filledPhoneNumber?.number) {
      // console.log("Pre-filling Skipping", filledPhoneNumber)
      return
    }

    const phoneNumber = profile.phoneNumber

    if (phoneNumber?.code && phoneNumber?.number) {
      const normalizedCode = `+${phoneNumber.code.toString().replace("+", "")}`

      const codeValue = countryCodes.find(
        c => c.dialCode === normalizedCode
      )?.dialCode

      if (codeValue) {
        setValue("phoneNumber", {
          code: codeValue,
          number: phoneNumber.number,
        })
        return
      }
    }

    if (defaultDialCode) {
      setValue("phoneNumber", {
        code: defaultDialCode,
        number: "",
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, defaultDialCode])

  const { isAuthenticated, setAuthInfo } = useGlobalContext()

  const [emailAlreadyExists, setEmailAlreadyExists] = useState<boolean>(false)

  const getUser = async (): Promise<FetchUserResponse | null> => {
    try {
      const res = await api.users.getUserDetails({
        data: {
          userId: getValues("email").trim(),
          product: 1,
        },
      })

      setEmailAlreadyExists(true)
      return res
    } catch (_) {
      setEmailAlreadyExists(false)
      return null
    }
  }

  const { executeGoogleReCaptcha } = useGoogleReCaptcha(
    import.meta.env.VITE_INVISIBLE_RECAPTCHA_KEY,
    {
      enterprise: true,
    }
  )

  const createBillingAddress = async () => {
    const location = getValues("location") ?? []

    const countryComponent = location.find(item =>
      item.types.includes(AddressTypeEnum.COUNTRY)
    )

    const stateComponent = location.find(item =>
      item.types.includes(AddressTypeEnum.STATE)
    )

    const cityComponent = location.find(item =>
      item.types.includes(AddressTypeEnum.CITY)
    )

    const fullName = getValues("fullName").trim()
    const email = getValues("email").trim()
    const phoneNumber =
      getValues("phoneNumber").code + getValues("phoneNumber").number

    const res = await api.payment.createAddress({
      data: {
        fullName,
        email,
        phoneNumber,
        address: null,
        location: cityComponent?.longText ?? stateComponent?.longText ?? null,
        state:
          stateComponent !== undefined
            ? {
                name: stateComponent.longText,
                isoCode: stateComponent.shortText,
              }
            : null,
        country:
          currentCountry !== null && currentCountry.isFallback === false
            ? {
                name: currentCountry.name,
                isoCode: currentCountry.isoCode,
              }
            : {
                ...(countryComponent?.longText
                  ? { name: countryComponent.longText }
                  : {}),
                ...(countryComponent?.shortText
                  ? { isoCode: countryComponent.shortText }
                  : {}),
              },
      },
    })

    return {
      billingAddressId: res.uuid,
      countryCode: countryComponent?.shortText || null,
      fullName,
      email,
      phoneNumber,
    }
  }

  const signUpUser = async () => {
    const captcha = {
      value: await safeCaptcha(executeGoogleReCaptcha, "auth"),
      type: CaptchaType.invisible,
    }

    if (!captcha.value) {
      toast.error("Please verify the captcha")
      return
    }

    // Track sign-up attempt
    trackingService.trackEvent(
      TrackingEvents.suraasaPlusSignUpAttempted,
      {
        email: getValues("email").trim(),
      },
      { services: ["PostHog"] }
    )

    try {
      const res = await api.auth.signUp({
        data: {
          captcha,
          email: getValues("email").trim(),
          first_name: getValues("fullName")?.trim().split(" ")[0] || "",
          last_name: getValues("fullName")
            ?.trim()
            .split(" ")
            .slice(1)
            .join(" "),
          phoneNumber:
            getValues("phoneNumber").code + getValues("phoneNumber").number,
          token_type: 1,
          platform: platform,
        },
      })

      trackingService.initUser(res)

      await createLead({
        email: res.user.email,
        fullName: getValues("fullName") || "",
        phoneNumber:
          getValues("phoneNumber").code + getValues("phoneNumber").number,
        originDetails: {
          brand: "suraasa",
          product: "suraasa_plus",
          activityMode: "online",
          activityChannel: "website",
          activityType: "product_purchase",
          activityDetail: "teacher_career_resolution_2026",
        },
        data: {
          uuid: res.user.uuid,
          source: trackingService.getSignupSource(),
        },
      })

      await createBillingAddress()

      setAuthInfo(res, { skipRemount: true })

      return true
    } catch (err) {
      mapErrorsV2(err, {
        setter: setError,
        fields: [["email"], ["fullName"], ["location"], ["phoneNumber"]],
      })

      return false
    }
  }

  const createUserAndAddress = async (data: FormType) => {
    const captcha = {
      value: await safeCaptcha(executeGoogleReCaptcha, "auth"),
      type: CaptchaType.invisible,
    }

    if (!captcha.value) {
      toast.error("Please verify the captcha")
      return null
    }

    try {
      let existingUser: FetchUserResponse | null = null

      if (isEmail(data.email)) {
        existingUser = await getUser()
      }

      const { countryCode, billingAddressId } = await createBillingAddress()

      // Track form completion
      const eventData = {
        email: data.email.trim(),
        full_name: data.fullName.trim(),
        country_code: countryCode,
        is_authenticated: isAuthenticated,
      }

      trackingService.trackEvent(
        TrackingEvents.suraasaPlusUserDetailsFormCompleted,
        eventData,
        { services: ["PostHog"] }
      )

      if (isAuthenticated) {
        return {
          billingAddressId,
          isUserless: false,
        }
      }

      if (!existingUser && data.fullName) {
        const success = await signUpUser()
        if (!success) return null
        return {
          billingAddressId,
          isUserless: false,
        }
      }

      // Mark the flow as userless and send to next step
      return {
        billingAddressId,
        isUserless: true,
      }
    } catch (err) {
      mapErrorsV2(err, {
        setter: setError,
        fields: [["email"], ["fullName"], ["phoneNumber"]],
      })

      return null
    }
  }

  const onFormSubmit = handleSubmit(async data => {
    const config = await createUserAndAddress(data)
    if (!config) {
      toast.error(
        "Something went wrong while submitting the form. Please try again."
      )
      console.warn("> SPNY: Failed to submit form")
      return
    }

    searchParams.set("full_name", data.fullName)
    searchParams.set("email", data.email)
    searchParams.set("phone_number", data.phoneNumber.number)
    searchParams.set(
      "dial_code",
      data.phoneNumber.code.toString().replaceAll("+", "")
    )

    setSearchParams(searchParams, { replace: true })

    // Call checkout
    if (!selectedProduct) {
      toast.error("No product selected. Please refresh the page.")
      throw new Error("No product selected")
    }
    await handleCheckout({
      chosenProduct: selectedProduct,
      isUserless: config.isUserless,
      addressId: config.billingAddressId,
    })
  })
  /**
   * Billing Information Component End
   */

  if (isActive) {
    return <AlreadyPaidScreen productSlug={SPNY_26_SLUG} />
  }

  if (paymentUnderProcess) {
    return (
      <PaymentUnderProcess
        onComplete={() => {
          setPaymentUnderProcess(false)
          enrollments.refetch()
        }}
      />
    )
  }

  const handleCheckout = async ({
    chosenProduct,
    isUserless,
    addressId,
  }: {
    chosenProduct: SuraasaPlusProduct
    isUserless: boolean
    addressId: string
  }) => {
    if (!addressId) {
      toast.error("Your session has expired. Please re-enter your details.")
      return
    }

    const planMode = chosenProduct.subscriptionDetails
      ? getPlanModeBySubscriptionIntervalType(chosenProduct.subscriptionDetails)
      : null

    searchParams.set("product_id", chosenProduct.id.toString())
    if (planMode) searchParams.set("subscription_type", planMode)
    searchParams.set("currency", chosenProduct.price.currency.code)
    searchParams.set(
      "price",
      (promo ? promo.priceAfterDiscount : chosenProduct.price.price).toString()
    )

    try {
      // Track order creation
      trackingService.trackEvent(
        TrackingEvents.suraasaPlusOrderCreated,
        {
          product_id: chosenProduct.id,
          product_slug: chosenProduct.slug,
          subscription_type: chosenProduct.subscriptionDetails
            ? getPlanModeBySubscriptionIntervalType(
                chosenProduct.subscriptionDetails
              )
            : null,
          price: promo ? promo.priceAfterDiscount : chosenProduct.price.price,
          currency: chosenProduct.price.currency.code,
          is_userless: isUserless ? "true" : "false",
          promo_code: promo?.code || null,
        },
        { services: ["PostHog"] }
      )
      const orderResponse = await payment.createOrder({
        isUserless: isUserless,
        billingAddressId: addressId,
        productSlug: chosenProduct.slug,
        promocode: promo?.code || undefined,
      })

      const paymentPlanId = orderResponse
        ? "order" in orderResponse
          ? orderResponse.order.paymentPlans[0].id
          : orderResponse.paymentPlans[0].id
        : null

      const userlessOrderToken = orderResponse
        ? "token" in orderResponse
          ? orderResponse.token
          : null
        : null

      if (userlessOrderToken)
        searchParams.set("order_token", userlessOrderToken)

      setSearchParams(searchParams, { replace: true })

      if (paymentPlanId)
        await payment.checkout({
          paymentPlanId,
          userlessOrderToken,
          promocode: promo?.code || undefined,
        })
    } catch (e) {
      mapErrorsV2(e)
    }
  }

  return (
    <PageContainer className="pb-5">
      <div className="absolute left-0 top-0 -z-1 hidden size-full w-1/2 bg-[linear-gradient(180deg,#FAFAFA_73.44%,#FAF4E5_100%)] md:block"></div>

      <div className="mb-4 mt-2">
        <img
          src="https://assets.suraasa.com/logos/v2/mini.webp"
          alt="suraasa logo"
          height={35}
          width={35}
          className="block md:hidden"
        />
        <img
          src="https://assets.suraasa.com/logos/v2/full-dark.webp"
          alt="suraasa logo"
          width={120}
          className="hidden md:block"
        />
      </div>

      <div className="flex flex-col-reverse justify-between gap-2 md:flex-row md:gap-10">
        {selectedProduct && payment.stripePaymentElement}

        {selectedProduct ? (
          <SelectPlan
            selectedProduct={selectedProduct}
            setSelectedPlan={setSelectedPlan}
            className="flex-1"
            products={products}
            checkout={onFormSubmit}
          />
        ) : (
          <div className="flex flex-1 flex-col gap-2">
            <div className="h-6 w-1/3 animate-pulse rounded-md bg-onSurface-200"></div>
            <div className="h-4 w-2/3 animate-pulse rounded-md bg-onSurface-200"></div>
            <div className="h-20 w-full animate-pulse rounded-md bg-onSurface-200"></div>
          </div>
        )}

        <Divider className="mb-2 md:hidden" />

        {/* Billing Information */}
        <div className={cn("flex flex-col flex-1")}>
          <ProductBanner className="mb-5 md:hidden" />

          <div className="flex flex-1 flex-col justify-center md:mb-0">
            <Typography className="text-[#4A4A38]" variant="largeBody">
              Billing Information
            </Typography>

            <form className="mt-2 flex flex-col gap-2">
              <TextField
                required={true}
                disabled={isAuthenticated && emailAlreadyExists}
                label="Full Name"
                placeholder="Enter your first and last name"
                errors={errors.fullName?.message}
                {...register("fullName", {
                  required: { value: true, message: "Required" },
                })}
              />
              <div>
                <InputLabel label={`Phone Number`} required />
                <Controller
                  control={control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <SplitTextField
                      fullWidth
                      error={Boolean(errors.phoneNumber)}
                      helperText={
                        errors.phoneNumber?.number?.message ||
                        errors.phoneNumber?.code?.message ||
                        errors.phoneNumber?.message
                      }
                      selectProps={{
                        placeholder: "Select",
                        options,
                        value: options.find(
                          c => c.value === field?.value?.code
                        ),
                        onChange: val => {
                          if (val)
                            field.onChange({
                              ...field.value,
                              code: val.value,
                            })
                        },
                      }}
                      textFieldProps={{
                        type: "number",
                        placeholder: "Enter phone number",
                        value: field?.value?.number ?? undefined,
                        onChange: val => {
                          field.onChange({
                            ...field.value,
                            number: val.target.value,
                          })
                        },
                      }}
                    />
                  )}
                  rules={{
                    validate: v => {
                      if (!v || (!v.code && !v.number)) return "Required"
                      if (!v.code) return "Country code is required"
                      if (!v.number) return "Phone Number is required"
                    },
                  }}
                />
              </div>
              <TextField
                required
                label="Email Address"
                disabled={isAuthenticated && emailAlreadyExists}
                type="email"
                placeholder="E.g., name@example.com"
                {...register("email", {
                  required: { value: true, message: "Required" },
                  validate: value => isEmail(value) || "Invalid email address",
                })}
                errors={errors.email?.message}
              />
              {(currentCountry === null || currentCountry.isFallback) && (
                <Controller
                  control={control}
                  name="location"
                  render={({ field: { onChange } }) => (
                    <GoogleLocationSelect
                      fullWidth
                      label="Billing Address"
                      required
                      className="mb-2 w-full"
                      placeHolder="Enter your location"
                      errors={errors.location?.message}
                      onChange={onChange}
                    />
                  )}
                  rules={{
                    required: { value: true, message: "Required" },
                  }}
                />
              )}

              {showPromoCodeInput ? (
                <>
                  <div
                    className={clsx("flex items-end gap-1", {
                      "items-center": !!errors.promocode?.message,
                    })}
                  >
                    <TextField
                      disabled={promo !== undefined}
                      label={promo ? "Promo Code" : "Promo Code (optional)"}
                      type="text"
                      placeholder="Enter promo code"
                      {...register("promocode")}
                      errors={errors.promocode?.message}
                    />

                    <AsyncBuilder
                      handler={async () => {
                        clearErrors("promocode")

                        if (promo) {
                          setValue("promocode", "")
                          await productGroupQuery.mutateAsync({
                            promocode: undefined,
                          })
                          return
                        }
                        const code = getValues("promocode")?.trim()
                        if (!code) {
                          setError("promocode", {
                            type: "manual",
                            message: "Please enter a promo code",
                          })
                          return
                        }
                        await productGroupQuery.mutateAsync({
                          promocode: code,
                        })
                      }}
                      render={props => (
                        <Button
                          color={promo ? "critical" : "black"}
                          size="sm"
                          variant="outlined"
                          className="mt-0.5 !py-1.25"
                          {...props}
                        >
                          {promo ? "Remove" : "Apply"}
                        </Button>
                      )}
                    />
                  </div>
                  {promo && (
                    <Typography
                      className="-mt-1.75 text-success-500"
                      variant="smallBody"
                    >
                      Promo code applied!
                    </Typography>
                  )}
                </>
              ) : (
                <Button
                  className="-ms-1 me-auto"
                  variant="text"
                  onClick={() => setShowPromoCodeInput(true)}
                >
                  Have a Promo Code?
                </Button>
              )}

              <div id="single-page-checkout-submit-button" />
            </form>
          </div>
        </div>
      </div>
    </PageContainer>
  )
}

export default SinglePageCheckout
